import { Head, Link, usePage } from '@inertiajs/react'

import { AppShell } from '@/layouts/app-shell'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { dashboardPath } from '@/routes'

type Organization = {
  id: string
  name: string
  slug: string
  logo?: string | null
}

type Repository = {
  id: string
  fullName: string
  name?: string
  owner?: string
}

type Props = {
  organizations: Organization[]
  repositories?: Repository[]
}

export default function SetupShow({ organizations, repositories = [] }: Props) {
  const { flash } = usePage().props

  return (
    <>
      <Head title="Setup" />
      <AppShell title="Setup">
        <div className="mx-auto w-full max-w-2xl space-y-6">
          <div>
            <h1 className="font-display text-2xl font-extrabold tracking-tight">Setup</h1>
            <p className="text-muted-foreground mt-2 text-sm">
              We couldn&apos;t find a workspace for your account yet. Contact support and we&apos;ll help you get
              connected.
            </p>
          </div>

          {(flash?.alert || flash?.notice) && (
            <p className={`text-sm ${flash.alert ? 'text-red-600' : 'text-golden-700'}`}>
              {flash.alert || flash.notice}
            </p>
          )}

          <Card>
            <CardHeader>
              <CardTitle>Workspaces</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              {organizations.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                  No workspaces are linked to this account. Email{' '}
                  <a href="mailto:raul@sunergos.ro" className="text-golden-700 underline">
                    raul@sunergos.ro
                  </a>{' '}
                  and we&apos;ll help.
                </p>
              ) : (
                organizations.map((org) => (
                  <div key={org.id} className="flex items-center gap-3 border-b border-border py-2 last:border-0">
                    {org.logo ? (
                      <img src={org.logo} alt="" className="h-8 w-8 rounded" />
                    ) : (
                      <span className="bg-muted flex h-8 w-8 items-center justify-center rounded text-xs font-semibold">
                        {org.name.slice(0, 2).toUpperCase()}
                      </span>
                    )}
                    <div>
                      <p className="font-medium">{org.name}</p>
                      <p className="text-muted-foreground text-xs">{org.slug}</p>
                    </div>
                  </div>
                ))
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Repositories</CardTitle>
            </CardHeader>
            <CardContent>
              {repositories.length === 0 ? (
                <p className="text-muted-foreground text-sm">No repositories loaded for this view yet.</p>
              ) : (
                <ul className="divide-y divide-border">
                  {repositories.map((repo) => (
                    <li key={repo.id} className="py-2 text-sm font-medium">
                      {repo.fullName}
                    </li>
                  ))}
                </ul>
              )}
            </CardContent>
          </Card>

          <Link href={dashboardPath()}>
            <Button variant="outline">Back to workspaces</Button>
          </Link>
        </div>
      </AppShell>
    </>
  )
}
